import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileChip from './FileChip.react';
import {
	BANK_TRANSFER_ALLOWED_MIME_TYPES,
	BANK_TRANSFER_MAX_FILE_SIZE_BYTES,
	BANK_TRANSFER_MAX_FILES,
} from '@arbitrip/constants';

const BankTransferUpload = ({ files, onFilesAdded, onFileRemoved }) => {
	const { t } = useTranslation();
	const fileInputRef = useRef(null);

	const [fileError, setFileError] = useState(null);
	const [isDragging, setIsDragging] = useState(false);

	const maxSizeMb = BANK_TRANSFER_MAX_FILE_SIZE_BYTES / (1024 * 1024);

	const validateFile = (file) => {
		if (!BANK_TRANSFER_ALLOWED_MIME_TYPES.includes(file.type)) {
			return t('Bookings.SettlePayment.BankTransferInvalidFileType');
		}
		if (file.size > BANK_TRANSFER_MAX_FILE_SIZE_BYTES) {
			return t('Bookings.SettlePayment.BankTransferFileTooLarge');
		}
		return null;
	};

	const addFiles = (fileList) => {
		const incoming = Array.from(fileList || []);
		if (!incoming.length) return;

		for (const file of incoming) {
			const error = validateFile(file);
			if (error) {
				setFileError(error);
				return;
			}
		}

		if (files.length + incoming.length > BANK_TRANSFER_MAX_FILES) {
			setFileError(t('Bookings.SettlePayment.BankTransferTooManyFiles', { max: BANK_TRANSFER_MAX_FILES }));
			return;
		}

		setFileError(null);
		onFilesAdded(incoming);
	};

	const handleDrop = (e) => {
		e.preventDefault();
		setIsDragging(false);
		addFiles(e.dataTransfer.files);
	};

	return (
		<>
			<div className="bank-transfer__upload-title">{t('Bookings.SettlePayment.UploadProofOfTransfer')}</div>

			<div
				className={`bank-transfer__dropzone${isDragging ? ' dragging' : ''}`}
				onDragOver={(e) => {
					e.preventDefault();
					setIsDragging(true);
				}}
				onDragLeave={() => setIsDragging(false)}
				onDrop={handleDrop}
			>
				<CloudUploadIcon fontSize="large" />
				<div className="bank-transfer__dropzone-text">
					<div>{t('Bookings.SettlePayment.DropFilesHere')}</div>
					<div>
						{/* The file-type list is wrapped in an isolated LTR <bdi> so the Unicode bidi
						algorithm can't reorder it when this renders inside RTL (Hebrew) text. */}
						<Trans
							i18nKey="Bookings.SettlePayment.DropFilesHereFileTypes"
							values={{ maxSize: maxSizeMb }}
							components={[<bdi key="file-types" dir="ltr" />]}
						/>
					</div>
				</div>
				<Button
					variant="outlined"
					className="bank-transfer__upload-button"
					onClick={() => fileInputRef.current?.click()}
				>
					{t('Bookings.SettlePayment.UploadProofOfPayment')}
				</Button>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					hidden
					className="bank-transfer__file-input"
					accept={BANK_TRANSFER_ALLOWED_MIME_TYPES.join(',')}
					onChange={(e) => {
						addFiles(e.target.files);
						e.target.value = '';
					}}
				/>
			</div>

			{fileError && <div className="bank-transfer__file-error">{fileError}</div>}

			{files.length > 0 && (
				<div className="bank-transfer__file-list">
					{_.chunk(files, 2).map((pair, rowIndex) => (
						<div className="bank-transfer__file-row" key={rowIndex}>
							{pair.map((file, i) => (
								<FileChip key={`${file.name}-${rowIndex * 2 + i}`} file={file} onDelete={() => onFileRemoved(rowIndex * 2 + i)} />
							))}
						</div>
					))}
				</div>
			)}
		</>
	);
};

export default BankTransferUpload;
